import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSeekerAuth } from "@/context/SeekerAuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function SeekerClaim() {
    const { token } = useParams();
    const branding = useBranding();
    const { refresh } = useSeekerAuth();
    const nav = useNavigate();
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/seeker/claim/${token}`);
                setPreview(data);
            } catch (err) {
                setError(formatApiError(err));
            } finally { setLoading(false); }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            await api.post("/seeker/claim", { token, password, name: preview?.name });
            await refresh();
            toast.success("Welcome aboard!");
            nav("/seeker/profile");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="claim-brand">
                    <BrandLogo size={32} />
                    <span className="font-display font-black text-xl">{branding.short_name}</span>
                </Link>

                <div className="bg-white border border-border p-8 space-y-6">
                    {loading ? (
                        <div className="text-sm text-muted-foreground font-mono">Loading invite...</div>
                    ) : error ? (
                        <div className="space-y-3 text-center">
                            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
                            <h1 className="font-display font-bold text-2xl">Invite unavailable</h1>
                            <p className="text-sm text-muted-foreground" data-testid="claim-error">{error}</p>
                            <Link to="/seeker/login" className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline inline-block mt-3">
                                Sign in to your portal →
                            </Link>
                        </div>
                    ) : preview?.already_registered ? (
                        <div className="space-y-3 text-center">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                            <h1 className="font-display font-bold text-2xl">You already have an account</h1>
                            <p className="text-sm text-muted-foreground">Sign in with the password you set previously.</p>
                            <Link to="/seeker/login" className="inline-block">
                                <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white">Sign in →</Button>
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div>
                                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Claim your profile</div>
                                <h1 className="font-display font-black text-3xl mt-2">Welcome, {preview?.name?.split(" ")[0] || "there"}.</h1>
                                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                                    <span className="inline-flex items-center gap-1.5 mr-1"><Building2 className="w-3.5 h-3.5 text-ikb" /></span>
                                    <strong>{preview?.company?.name || "A hiring team"}</strong> uploaded your profile and invited you
                                    to take it over. Set a password to access your dashboard, track applications, and message recruiters directly.
                                </p>
                                <div className="text-xs font-mono text-muted-foreground mt-3 border border-border bg-secondary p-2" data-testid="claim-email">
                                    {preview?.email}
                                </div>
                            </div>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest font-bold">Choose a password</Label>
                                    <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none mt-2 h-11" data-testid="claim-password" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest font-bold">Confirm password</Label>
                                    <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none mt-2 h-11" data-testid="claim-password-confirm" />
                                </div>
                                <Button type="submit" disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11" data-testid="claim-submit">
                                    {busy ? "Creating..." : "Claim & sign in →"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
